<template>
  <transition name="menu">
    <div
      v-if="open"
      class="z-50 font-body bg-n-900/80 sm:backdrop-blur-sm absolute top-0 left-0 w-screen h-screen flex justify-end overflow-hidden"
      @click="($emit('close'), (showSettings = false))"
    >
      <div
        class="sidebar bg-n-0 dark:bg-n-800 text-n-900 dark:text-n-0 w-full sm:w-80 h-full flex flex-col overflow-hidden pb-safe"
        @click.stop
      >
        <div class="bg-n-100 dark:bg-n-900 pt-safe">
          <div class="flex justify-end px-4 items-center h-14">
            <l-button
              size="medium"
              icon="close"
              type="text"
              :color="darkMode ? 'white' : 'primary'"
              @click="($emit('close'), (showSettings = false))"
            />
          </div>
        </div>
        <div class="flex-1 relative">
          <transition :name="showSettings ? 'menu-left' : 'menu-right'">
            <div v-if="!showSettings" class="absolute inset-0 w-full flex flex-col">
              <div class="flex-1 flex flex-col px-5 overflow-hidden">
                <div class="overflow-y-scroll flex-1">
                  <div class="py-3">
                    <p class="text-xs text-n-600 dark:text-n-400 mb-2 uppercase font-semibold">
                      {{ t('sidemenu.printer') }}
                    </p>
                    <l-select :options="printerList" v-model="selectedPrinterId" />
                  </div>

                  <div class="py-3">
                    <p class="text-xs text-n-600 dark:text-n-400 mb-2 uppercase font-semibold">
                      {{ t('sidemenu.displayMode') }}
                    </p>
                    <l-select
                      v-model="screenConfig.mode"
                      :options="modeList"
                    />
                  </div>

                  <div class="py-3">
                    <p class="text-xs text-n-600 dark:text-n-400 mb-2 uppercase font-semibold">
                      {{ t('sidemenu.options') }}
                    </p>
                    <l-checkbox v-model="screenConfig.showSummary" class="text-n-900 dark:text-n-0">
                      {{ t('sidemenu.show-summary') }}
                    </l-checkbox>
                  </div>

                  <div
                    class="py-3 flex items-center cursor-pointer gap-2"
                    @click="showSettings = true"
                  >
                    <l-icon name="settings" />
                    {{ t('configuration.settings') }}
                  </div>
                </div>
                <div class="flex-none flex-col w-full mb-2">
                  <div class="flex flex-row items-center justify-center">
                    <div class="text-xs">{{ t('sidemenu.version') }} {{ appVersion }}</div>
                    <l-icon class="px-1" name="question-circle" />
                  </div>
                </div>
              </div>
            </div>
            <settings
              v-else-if="showSettings"
              @back="showSettings = false"
              class="absolute inset-0"
            />
          </transition>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { LButton, LCheckbox, LIcon, LSelect } from '@last/core-ui/paprika'

import { useConfigStore } from '@/store/config'

import Settings from '../modules/Settings.vue'

defineProps<{
  open: boolean
}>()

defineEmits<{
  close: []
}>()

const { t } = useI18n()

const configStore = useConfigStore()

const { appVersion, printers, selectedPrinterId, screenConfig, darkMode } = storeToRefs(configStore)

const showSettings = ref(false)

const printerList = computed(() => {
  const allPrinters = Object.values(printers.value).map(printer => ({
    label: printer.name,
    value: printer.id
  }))

  return [
    {
      label: t('sidemenu.all-printers'),
      value: 'all'
    },
    ...allPrinters
  ]
})

const modeList = computed(() => [
  {
    label: t('sidemenu.mode.horizontal'),
    value: 'horizontal'
  },
  {
    label: t('sidemenu.mode.masonry'),
    value: 'masonry'
  },
  {
    label: t('sidemenu.mode.expo'),
    value: 'expo'
  },
  {
    label: t('sidemenu.mode.allday'),
    value: 'allday'
  }
])
</script>

<style scoped>
.menu-enter-active,
.menu-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
}

.menu-enter-active .sidebar,
.menu-leave-active .sidebar {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-enter-from .sidebar,
.menu-leave-to .sidebar {
  transform: translateX(100%);
}

.menu-left-enter-active,
.menu-left-leave-active,
.menu-right-enter-active,
.menu-right-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-left-enter-to {
  transform: translateX(0);
}

.menu-left-leave-from {
  transform: translateX(0);
}

.menu-left-enter-from {
  transform: translateX(100%);
}

.menu-left-leave-to {
  transform: translateX(-100%);
}

.menu-right-enter-to {
  transform: translateX(0);
}

.menu-right-leave-from {
  transform: translateX(0);
}

.menu-right-enter-from {
  transform: translateX(-100%);
}

.menu-right-leave-to {
  transform: translateX(100%);
}
</style>
